import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, ProtectedRoute, AuthCallback } from "@/context/AuthContext";
import SplashPage from "@/components/SplashPage";
import AthleteHomePage from "@/components/AthleteHomePage";
import AdultHomePage from "@/components/AdultHomePage";
import HomePage from "@/components/HomePage";
import PhilosophyPage from "@/components/PhilosophyPage";
import LoginPage from "@/pages/LoginPage";
import LandingPage from "@/pages/LandingPage";
import PricingPage from "@/pages/PricingPage";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import DashboardPage from "@/pages/DashboardPage";
import JourneyPage from "@/pages/JourneyPage";
import BillingPage from "@/pages/BillingPage";
import WorkoutsPage from "@/pages/WorkoutsPage";
import ProgramsPage from "@/pages/ProgramsPage";
import AdminDashboard from "@/pages/AdminDashboard";
import { AIGenerator, AICoach, FormAnalysis, NutritionPlans, CoachingPage, CoachingSuccessPage } from "@/pages/AIPages";
import { Presentation } from "@/components/presentations/Presentation";
import JumpAssessment from "@/pages/JumpAssessment";
import AssessmentLogin from "@/pages/AssessmentLogin";
import AssessmentDashboard from "@/pages/AssessmentDashboard";
import TermsOfService from "@/components/legal/TermsOfService";
import PrivacyPolicy from "@/components/legal/PrivacyPolicy";
import CancellationPolicy from "@/components/legal/CancellationPolicy";
import HealthWaiver from "@/components/legal/HealthWaiver";
import JumpReportPage from "@/pages/JumpReportPage";
import AffiliateLogin from "@/pages/AffiliateLogin";
import AffiliateDashboard from "@/pages/AffiliateDashboard";
import FoundationsPage from "@/pages/FoundationsPage";
import FoundationsYouthPage from "@/pages/FoundationsYouthPage";
import FoundationsAdultsPage from "@/pages/FoundationsAdultsPage";
import HomeGymAcceleratorPage from "@/pages/HomeGymAcceleratorPage";
import SportsPerformancePage from "@/pages/SportsPerformancePage";
import CoachSachinPage from "@/pages/CoachSachinPage";

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Detect session_id synchronously during render (NOT in useEffect).
  // This handles the OAuth callback when user returns with #session_id=xxx
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<SplashPage />} />
      <Route path="/for-athletes" element={<AthleteHomePage />} />
      <Route path="/for-adults" element={<AdultHomePage />} />
      <Route path="/foundations" element={<FoundationsPage />} />
      <Route path="/foundations/youth" element={<FoundationsYouthPage />} />
      <Route path="/foundations/adults" element={<FoundationsAdultsPage />} />
      <Route path="/accelerator/home-gym" element={<HomeGymAcceleratorPage />} />
      <Route path="/accelerator/sports-performance" element={<SportsPerformancePage />} />
      <Route path="/coach-sachin" element={<CoachSachinPage />} />
      <Route path="/why-paladin" element={<CoachSachinPage />} />
      <Route path="/home" element={<HomePage />} />
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/philosophy" element={<PhilosophyPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/pricing" element={<Navigate to="/" replace />} />
      <Route path="/legal/terms" element={<TermsOfService />} />
      <Route path="/legal/privacy" element={<PrivacyPolicy />} />
      <Route path="/legal/cancellation" element={<CancellationPolicy />} />
      <Route path="/legal/waiver" element={<HealthWaiver />} />
      <Route path="/presentations/sports-science" element={<Presentation />} />
      <Route path="/assess/:token" element={<JumpAssessment />} />
      <Route path="/assessments/login" element={<AssessmentLogin />} />
      <Route path="/assessments/dashboard" element={<AssessmentDashboard />} />
      <Route path="/jump-report" element={<JumpReportPage />} />
      <Route path="/affiliate/login" element={<AffiliateLogin />} />
      <Route path="/affiliate/dashboard" element={<AffiliateDashboard />} />

      {/* Subscription Flow */}
      <Route path="/subscription/success" element={
        <ProtectedRoute requireSubscription={false}><SubscriptionSuccess /></ProtectedRoute>
      } />
      <Route path="/schedule/success" element={
        <ProtectedRoute requireSubscription={false}><Navigate to="/dashboard" replace /></ProtectedRoute>
      } />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute><DashboardPage /></ProtectedRoute>
      } />
      <Route path="/journey" element={
        <ProtectedRoute><JourneyPage /></ProtectedRoute>
      } />
      <Route path="/billing" element={
        <ProtectedRoute requireSubscription={false}><BillingPage /></ProtectedRoute>
      } />
      <Route path="/workouts" element={
        <ProtectedRoute><WorkoutsPage /></ProtectedRoute>
      } />
      <Route path="/programs" element={
        <ProtectedRoute><ProgramsPage /></ProtectedRoute>
      } />

      {/* AI Features */}
      <Route path="/ai-generator" element={
        <ProtectedRoute><AIGenerator /></ProtectedRoute>
      } />
      <Route path="/ai-coach" element={
        <ProtectedRoute><AICoach /></ProtectedRoute>
      } />
      <Route path="/form-analysis" element={
        <ProtectedRoute><FormAnalysis /></ProtectedRoute>
      } />
      <Route path="/nutrition" element={
        <ProtectedRoute><NutritionPlans /></ProtectedRoute>
      } />
      <Route path="/coaching" element={
        <ProtectedRoute><CoachingPage /></ProtectedRoute>
      } />
      <Route path="/coaching/success" element={
        <ProtectedRoute><CoachingSuccessPage /></ProtectedRoute>
      } />

      {/* Admin/Coach Routes */}
      <Route path="/admin" element={
        <ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>
      } />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" theme="dark" richColors />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
